/**
 * Login-funnel telemetry: detect users who try to log in and bounce back to the
 * login screen WITHOUT a session — silently, so the team is alerted without the
 * user having to report it. A marker is persisted when an attempt starts; when
 * the app later observes "no session", the marker + the callback URL classify
 * the outcome into a clear error code:
 *
 *  - LOGIN_USER_CANCELLED   — user dismissed the provider consent (normal; low alarm)
 *  - LOGIN_PROVIDER_ERROR   — provider/Supabase returned an explicit error (blocking)
 *  - LOGIN_STRANDED_RETURN  — user came back with NO session and NO error params:
 *                             the silent killer (e.g. the v1.8.6 Android App-Link
 *                             hijack) — exactly the case that must trigger an
 *                             investigation.
 *  - LOGIN_EXPLICIT_ERROR   — the sign-in API call itself rejected (logged at
 *                             the call site, not via the marker).
 *
 * Pure + storage-injectable so every path is unit-testable.
 */

export type LoginMethod = 'google' | 'email';

export interface PendingLoginAttempt {
  method: LoginMethod;
  startedAt: number;
  /** Where the attempt started (sanitized pathname only — never tokens). */
  fromPath: string;
}

export type LoginOutcomeCode =
  | 'LOGIN_USER_CANCELLED'
  | 'LOGIN_PROVIDER_ERROR'
  | 'LOGIN_STRANDED_RETURN';

export interface LoginFailure {
  code: LoginOutcomeCode;
  method: LoginMethod;
  /** Seconds between starting the attempt and the failure observation. */
  elapsedSeconds: number;
  /** Provider error details when present (already token-free). */
  providerError?: string;
  providerErrorDescription?: string;
}

export const PENDING_LOGIN_KEY = 'team_v_pending_login_attempt';

/**
 * An OAuth round-trip (consent screen etc.) legitimately takes a while; only
 * treat a session-less return as a failure once the attempt is older than this.
 */
const MIN_AGE_SECONDS = 10;
/** Markers older than this are stale (user gave up days ago) — drop silently. */
const MAX_AGE_HOURS = 24;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function safeStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Record that a login attempt is starting. Call right before the sign-in call. */
export function beginLoginAttempt(
  method: LoginMethod,
  now: Date = new Date(),
  storage: StorageLike | null = safeStorage(),
): void {
  if (!storage) return;
  const attempt: PendingLoginAttempt = {
    method,
    startedAt: now.getTime(),
    fromPath: typeof window === 'undefined' ? '' : window.location.pathname,
  };
  try {
    storage.setItem(PENDING_LOGIN_KEY, JSON.stringify(attempt));
  } catch {
    /* storage full/blocked — telemetry is best-effort */
  }
}

/** Clear the marker (on success, or after reporting a failure). */
export function clearLoginAttempt(storage: StorageLike | null = safeStorage()): void {
  try {
    storage?.removeItem(PENDING_LOGIN_KEY);
  } catch {
    /* ignore */
  }
}

export function readPendingAttempt(
  storage: StorageLike | null = safeStorage(),
): PendingLoginAttempt | null {
  try {
    const raw = storage?.getItem(PENDING_LOGIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingLoginAttempt;
    if (!parsed || typeof parsed.startedAt !== 'number' || !parsed.method) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Extract provider error params from a callback URL (hash or query). Supabase
 * forwards OAuth errors as `error`, `error_code`, `error_description`. Tokens
 * are never read here.
 */
export function extractProviderError(href: string): {
  error: string | null;
  description: string | null;
} {
  try {
    const url = new URL(href, 'https://x.invalid');
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const query = url.searchParams;
    const error = hash.get('error') ?? query.get('error');
    const description =
      hash.get('error_description') ?? query.get('error_description') ?? hash.get('error_code') ?? query.get('error_code');
    return { error, description };
  } catch {
    return { error: null, description: null };
  }
}

/**
 * True when applying a service-worker update (= a page reload) right now could
 * destroy a login in progress: OAuth tokens/errors are in the URL, or a login
 * attempt started moments ago. The PWA layer defers updates at these moments —
 * a silent update reload mid-callback was eating OAuth returns on stale shells.
 */
export function isLoginSensitiveMoment(
  href: string,
  pending: PendingLoginAttempt | null,
  now: Date = new Date(),
): boolean {
  if (/[#&?](access_token|refresh_token|error|code)=/.test(href)) return true;
  if (pending && now.getTime() - pending.startedAt < 2 * 60 * 1000) return true;
  return false;
}

/**
 * Evaluate the pending attempt against the current auth state. Returns a
 * failure to report, or null when there is nothing to do. The caller clears the
 * marker iff a non-null result is returned (or on success).
 *
 * - hasSession=true → success: caller clears, nothing reported.
 * - provider error in URL → classify cancel vs error immediately (any age).
 * - no session + attempt older than MIN_AGE_SECONDS → stranded return.
 * - attempt younger than MIN_AGE_SECONDS → still in flight → null.
 * - attempt older than MAX_AGE_HOURS → stale → caller clears, nothing reported.
 */
export function evaluatePendingAttempt(
  attempt: PendingLoginAttempt,
  hasSession: boolean,
  href: string,
  now: Date = new Date(),
): { action: 'clear' | 'wait'; failure: LoginFailure | null } {
  const elapsedSeconds = Math.round((now.getTime() - attempt.startedAt) / 1000);

  if (hasSession) return { action: 'clear', failure: null };

  if (elapsedSeconds > MAX_AGE_HOURS * 3600) return { action: 'clear', failure: null };

  const { error, description } = extractProviderError(href);
  if (error) {
    const cancelled = error === 'access_denied' || /cancel/i.test(description ?? '');
    return {
      action: 'clear',
      failure: {
        code: cancelled ? 'LOGIN_USER_CANCELLED' : 'LOGIN_PROVIDER_ERROR',
        method: attempt.method,
        elapsedSeconds,
        providerError: error,
        providerErrorDescription: description ?? undefined,
      },
    };
  }

  if (elapsedSeconds >= MIN_AGE_SECONDS) {
    return {
      action: 'clear',
      failure: {
        code: 'LOGIN_STRANDED_RETURN',
        method: attempt.method,
        elapsedSeconds,
      },
    };
  }

  return { action: 'wait', failure: null };
}
